<template>
  <div class="pb-1">
    <div class="flex items-center border p-1 px-4 border-primary-50 dark:border-dark-400 dark:bg-dark-500">
      <span class="mr-2 text-gray-600 dark:text-dark-text">
        {{ calc2digit(problemSearchModalStatePresenter.selectedCourse.value.progress) }}%
      </span>
      <base-progress-bar :num="calc2digit(problemSearchModalStatePresenter.selectedCourse.value.progress)" />
    </div>
    <div class="my-2 border rounded bg-white dark:border-dark-400 dark:bg-dark-400">
      <div class="flex mt-3">
        <div class="flex-grow" />
        <DropDown
          class="mr-3"
          :itemList="items"
          :selected="changeProgramLanguage"
          :selectedIndex="nested.selectedIndex.value"
        />
      </div>
      <CourseHeatMap
        class="pb-2 mx-4"
        :course="problemSearchModalStatePresenter.selectedCourse.value"
        height="400"
        :programLanguage="selectedProgramLanguage"
        :user="user"
      />
    </div>
    <div class="my-2 text-gray-900 dark:text-dark-text">
      <details
        v-for="topic in problemSearchModalStatePresenter.selectedCourse.value.topics"
        :key="topic.id"
        class="my-1"
      >
        <summary
          class="hover-hand py-3 pr-4 pl-12 border rounded relative hover:shadow transition-shadow duration-100 hover:border-course dark:border-dark-400 dark:text-dark-text dark:bg-dark-500 dark:hover:border-course-dark"
          :class="isLightMode() ? 'arrow' : 'arrow-dark'"
        >
          <div class="text-lg">
            <span>
              {{ topic.shortName }}
            </span>
            <span class="font-bold ml-4">
              {{ topic.name }}
            </span>
          </div>
          <div class="flex items-center mt-2 text-xs text-gray-500 dark:text-dark-text">
            <span class="mr-2">{{ calc2digit(topic.progress) }}%</span>
            <base-progress-bar :num="topic.progress" />
          </div>
        </summary>
        <div
          v-for="problem in topic.problems"
          :key="problem.id"
          class="hover-hand border rounded my-1 flex items-center p-4 hover:shadow transition duration-100 hover:border-course dark:border-dark-400 dark:text-dark-text dark:bg-dark-500 dark:hover:border-course-dark"
          @click="() => clickProblem(problem.id)"
        >
          <span class="material-icons mr-4">
            <span v-if="problem.isSolved" class="text-course dark:text-course-dark"> check_circle </span>
            <span v-else class="text-gray-500 dark:text-dark-text"> radio_button_unchecked </span>
          </span>
          <p class="text-lg font-bold">{{ problem.id }} : {{ problem.name }}</p>
        </div>
      </details>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, Ref, ref } from 'vue'

import BaseProgressBar from './BaseProgressBar.vue'

import { ProblemPath } from '@/domain/entities/problem/problemPath'
import { ProgramLanguageType } from '@/domain/value_objects/language/programLanguageType'
import CourseHeatMap from '@/presentation/components/Dashboard/children/course/CourseHeatMap.vue'
import DropDown from '@/presentation/components/DropDown/DropDown.vue'
import { useAuthStatePresenter } from '@/presentation/composables/auth/useAuthStatePresenter'
import { useConfigStatePresenter } from '@/presentation/composables/config/useConfigStatePresenter'
import { useCourseStatisticsPresenter } from '@/presentation/composables/problem/useCourseStatisticsPresenter'
import { useProblemSearchModalStatePresenter } from '@/presentation/composables/problem/useProblemSearchModalStatePresenter'

export default defineComponent({
  components: {
    BaseProgressBar,
    CourseHeatMap,
    DropDown
  },
  props: {
    onClickProblem: {
      type: Function,
      required: true
    }
  },
  setup(props, ctx) {
    const problemSearchModalStatePresenter = useProblemSearchModalStatePresenter()
    const courseStatisticsPresenter = useCourseStatisticsPresenter()
    courseStatisticsPresenter.clearCourseStatisticsList()
    const { user } = useAuthStatePresenter()
    const { isLightMode } = useConfigStatePresenter()

    const selectedProgramLanguage: Ref<ProgramLanguageType | 'All'> = ref('All')

    const changeProgramLanguage = async (index: number, programLanguage: ProgramLanguageType | 'All') => {
      selectedProgramLanguage.value = programLanguage
    }

    const calc2digit = (num: number) => {
      return Math.floor((num + 0.005) * 100) / 100
    }

    const clickProblem = (id: string) => {
      const path: ProblemPath = {
        course: problemSearchModalStatePresenter.selectedCourse.value.id,
        problem: id
      }
      props.onClickProblem(id, path)
    }

    const selectedIndex = ref(0)
    const items: (ProgramLanguageType | 'All')[] = [
      'All',
      'C',
      'C++',
      'C#',
      'D',
      'JAVA',
      'Scala',
      'Python',
      'Ruby',
      'Rust',
      'JavaScript',
      'Go',
      'Kotlin',
      'PHP',
      'Haskell',
      'OCaml'
    ]

    return {
      problemSearchModalStatePresenter,
      selectedProgramLanguage,
      user,
      items,
      calc2digit,
      clickProblem,
      changeProgramLanguage,
      isLightMode,
      nested: { selectedIndex }
    }
  }
})
</script>

<style scoped>
summary {
  display: block;
}
summary::-webkit-details-marker {
  display: none;
}

.open-anime {
  animation: fadeIn 0.5s ease;
}
@keyframes fadeIn {
  0% {
    opacity: 0;
    transform: translateY(-20px);
  }
  100% {
    opacity: 1;
    transform: none;
  }
}

.arrow::before {
  content: '';
  position: absolute;
  top: 1.8rem;
  left: 1rem;
  width: 1rem;
  height: 1rem;
  border-top: 0.5rem solid white;
  border-bottom: 0.5rem solid white;
  border-left: 0.5rem solid #b0bec5;
  box-sizing: border-box;
  transition: 200ms;
}

details[open] .arrow::before {
  transform: rotate(90deg);
  left: 1rem;
  top: 1.8rem;
}

.arrow-dark::before {
  content: '';
  position: absolute;
  top: 1.8rem;
  left: 1rem;
  width: 1rem;
  height: 1rem;
  border-top: 0.5rem solid #2e2e2e;
  border-bottom: 0.5rem solid #2e2e2e;
  border-left: 0.5rem solid #b0bec5;
  box-sizing: border-box;
  transition: 200ms;
}

details[open] .arrow-dark::before {
  transform: rotate(90deg);
  left: 1rem;
  top: 1.8rem;
}

.hover-hand {
  cursor: pointer;
  cursor: hand;
}
</style>
